import { Client } from "@/Common/Api/Client";
import { EmailContact } from "@/Common/Api/Contacts/Dto/EmailContact";
import { PhoneNumber } from "@/Common/Api/Contacts/Dto/PhoneNumber";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { ButtonControl } from "@/Common/Components/Buttons/ButtonControl";
import ButtonGroup from "@/Common/Components/Buttons/ButtonGroup";
import { TextboxMaskedControl } from "@/Common/Components/Inputs/TextboxMaskedControl";
import { Modal } from "@/Common/Components/Modals/Modal";
import { ValidationHelper } from "@/Common/Helpers/ValidationHelper";
import { ContactPointType } from "@/Common/Models/Contacts/Enums/ContactPointType";
import { PhoneType } from "@/Common/Models/Contacts/Enums/PhoneType";
import { Person } from "@/Common/Models/Person/Person";
import * as ServerOperations from "@/Common/Operations/operations.server";

interface IAddContactPointWarningProps {
    patient: Person;

    onClose: () => void;
    onContinue: (emailContactPointId?: string, phoneContactPointId?: string) => void;
    onContinueWithoutContact: () => void;
}

class AddContactPointWarningState {
    public email: EmailContact;
    public phone: PhoneNumber;
}

class AddContactPointWarningPopup extends BaseReactComponent<IAddContactPointWarningProps, AddContactPointWarningState> {
    private componentId: string = "add-contactpoint-popup";
    private emailFieldId: string = "add-email-textbox";
    private phoneFieldId: string = "add-phone-textbox";
    private isSMSEnabled: boolean = cacheStore.portalConfiguration?.HasSmsNotificationEnabled;

    private validationHelper = new ValidationHelper();
    private emailValidationRule = this.validationHelper.getEmailValidationRule();
    private emailMask: string = this.emailValidationRule ? this.emailValidationRule.EditMaskPattern : "";
    private emailValidationErrorMessage: string = (this.emailValidationRule && this.emailValidationRule.ValidationErrorMessageLookupId)
        ? cacheStore.GetLocalizedValueByLookupId(this.emailValidationRule.ValidationErrorMessageLookupId)
        : "";

    constructor(props: IAddContactPointWarningProps) {
        super(props);

        const initialState = new AddContactPointWarningState();
        initialState.email = EmailContact.newEmail();
        initialState.phone = PhoneNumber.newPhone(ContactPointType.Personal, PhoneType.Mobile);
        initialState.phone.IsValid = PhoneNumber.validatePhone(initialState.phone, true);

        this.state = initialState;
    }

    public render(): JSX.Element {
        return (
            <Modal
                id={this.componentId}
                title={cacheStore.GetString("WarningLabel")}
                onClose={() => this.close()}
                footer={this.renderFooter()}
                top={180}
                width={700}
                disableOverlayClose
                draggable
            >
                {this.renderContactForm()}
            </Modal>
        );
    }

    private renderContactForm(): JSX.Element {
        let addContactPrompt: string = this.isSMSEnabled
            ? cacheStore.GetString("AddQuestionnaireContactPromptLabel")
            : cacheStore.GetString("AddQuestionnaireEmailPromptLabel");

        return (
            <div>
                <p>{addContactPrompt}</p>
                <br />
                <p>{cacheStore.GetString("ContinueWithoutContactPromptLabel")}</p>
                <br />

                {this.isSMSEnabled && (
                    <div className="row" style={{ "marginBottom": "10px" }}>
                        <div className="col-lg-2 col-md-2 col-sm-2 col-center-align">
                            <label className="form-label" style={{ "paddingTop": "6px" }}>{cacheStore.GetString("PhoneLabel")}</label>
                        </div>

                        <div className="col-lg-10 col-md-10 col-sm-10">
                            <TextboxMaskedControl
                                id={this.phoneFieldId}
                                isValid={this.state.phone.IsValid && this.hasFieldsFilledOut()}
                                allowEmpty={false}
                                value={this.state.phone.Number}
                                onChangeFunction={value => this.onPhoneChanged(value)}
                                item={this.state.phone}
                                mask={PhoneNumber.getMask(this.state.phone)}
                                validationErrorMessage={PhoneNumber.getValidationErrorMessage(this.state.phone)}
                            />
                        </div>
                    </div>
                )}

                <div className="row" style={{ "marginBottom": "10px" }}>
                    <div className="col-lg-2 col-md-2 col-sm-2 col-center-align">
                        <label className="form-label" style={{ "paddingTop": "6px" }}>{cacheStore.GetString("EmailLabel")}</label>
                    </div>

                    <div className="col-lg-10 col-md-10 col-sm-10">
                        <TextboxMaskedControl
                            id={this.emailFieldId}
                            isValid={this.isSMSEnabled ? this.state.email.isValid() && this.hasFieldsFilledOut() : this.state.email.isValid(false)}
                            allowEmpty={false}
                            alphanumericIncludesPeriod={false}
                            returnMaskedValue={true}
                            value={this.state.email.Address}
                            onChangeFunction={value => this.mutateState(s => s.email.Address = value)}
                            item={this.state.email}
                            mask={this.emailMask}
                            validationErrorMessage={this.emailValidationErrorMessage}
                            isEmail={true}
                        />
                    </div>
                </div>
            </div>
        );
    }

    private renderFooter(): JSX.Element {
        return (
            <div className="p-footer flex-display flex-end">
                <ButtonGroup>
                    <ButtonControl
                        variant="neutral"
                        onClick={() => this.close()}
                        label={cacheStore.GetString("CancelLabel")}
                    />
                    <ButtonControl
                        variant="brand"
                        onClick={() => this.continueWithoutContact()}
                        label={cacheStore.GetString("ContinueWithoutContactLabel")}
                        dataCy="addContactPointWarningPopup-continueWithoutContact-button"
                    />
                    <ButtonControl
                        variant="brand"
                        disabled={!this.isFormValid()}
                        onClick={() => this.continue()}
                        label={cacheStore.GetString("ContinueLabel")}
                    />
                </ButtonGroup>
            </div>
        );
    }

    private onPhoneChanged(value: string): void {
        this.mutateState(s => {
            s.phone.Number = value;
            s.phone.IsValid = PhoneNumber.validatePhone(s.phone, true);
        });
    }

    private isFormValid(): boolean {
        return this.isSMSEnabled
            ? this.hasFieldsFilledOut() && this.state.email.isValid() && this.state.phone.IsValid
            : this.state.email.isValid(false);
    }

    private hasFieldsFilledOut(): boolean {
        return (!!this.state.email.Address || !!this.state.phone.Number);
    }

    private close(): void {
        this.props.onClose?.();
    }

    private continueWithoutContact(): void {
        this.props.onContinueWithoutContact?.();
    }

    private async continue(): Promise<void> {
        const response = await Client.postAsync<{ EmailContactPointId?: string, PhoneContactPointId?: string }>(
            ServerOperations.Server.Myle_Individual_AddContact,
            {
                ContactId: this.props.patient.ContactId,
                Phone: this.state.phone,
                Email: this.state.email
            }
        );

        if (response.isSuccess) {
            calendarStore.calendarActions.LoadPatient(calendarStore.appointmentEditModel);
            this.props.onContinue?.(response.data.EmailContactPointId, response.data.PhoneContactPointId);
        }
        else
            sessionStore.showPopup(response.errors);
    }
}

export { AddContactPointWarningPopup };
